/**
 * Utilidades para manejo de fechas en zona horaria chilena
 */

// Zona horaria de Chile
const CHILE_TIMEZONE = 'America/Santiago';

// Helpers
const isDateOnly = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const isMidnightISO = (s) => typeof s === 'string' && /T00:00:00(\.000)?Z?$/.test(s);

/**
 * Convierte una fecha a string formato YYYY-MM-DD en zona horaria chilena
 * @param {Date|string} date - Fecha a convertir
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const dateToChileanString = (date) => {
  if (!date) return '';
  
  // Si viene como YYYY-MM-DD o ISO a medianoche, devolvemos la parte de fecha tal cual
  if (typeof date === 'string') {
    if (isDateOnly(date)) return date;
    if (isMidnightISO(date)) return date.slice(0, 10);
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Crear fecha en zona horaria chilena
  const chileanDate = new Date(dateObj.toLocaleString('en-US', { timeZone: CHILE_TIMEZONE }));
  
  // Formatear como YYYY-MM-DD
  const year = chileanDate.getFullYear();
  const month = String(chileanDate.getMonth() + 1).padStart(2, '0');
  const day = String(chileanDate.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Convierte una fecha de string YYYY-MM-DD a Date object en zona horaria chilena
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {Date} Objeto Date
 */
export const chileanStringToDate = (dateString) => {
  if (!dateString) return null;
  
  // Parsear la fecha asumiendo zona horaria chilena
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Crear fecha en zona horaria chilena
  const date = new Date();
  date.setFullYear(year);
  date.setMonth(month - 1); // Los meses en JS son 0-indexados
  date.setDate(day);
  date.setHours(12, 0, 0, 0); // Mediodía para evitar problemas con DST
  
  return date;
};

/**
 * Formatea una fecha para mostrar en formato chileno (DD/MM/YYYY)
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
export const formatChileanDate = (date) => {
  if (!date) return '';
  
  // Si viene como YYYY-MM-DD o ISO a medianoche, formatemos por string para evitar desfases
  if (typeof date === 'string') {
    const ymd = isDateOnly(date) ? date : isMidnightISO(date) ? date.slice(0, 10) : null;
    if (ymd) {
      const [y, m, d] = ymd.split('-');
      return `${d}/${m}/${y}`;
    }
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('es-CL', {
    timeZone: CHILE_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formatea una fecha para mostrar en formato chileno con nombre del mes
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
export const formatChileanDateLong = (date) => {
  if (!date) return '';
  
  // Si viene como YYYY-MM-DD o ISO a medianoche, formatemos por string
  if (typeof date === 'string') {
    const ymd = isDateOnly(date) ? date : isMidnightISO(date) ? date.slice(0, 10) : null;
    if (ymd) {
      const [y, m, d] = ymd.split('-');
      // Nombre de mes en español corto
      const monthNames = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
      return `${Number(d)} de ${monthNames[Number(m) - 1]} de ${y}`;
    }
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString('es-CL', {
    timeZone: CHILE_TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Obtiene la fecha actual en zona horaria chilena
 * @returns {Date} Fecha actual en Chile
 */
export const getCurrentChileanDate = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: CHILE_TIMEZONE }));
};

/**
 * Convierte una fecha UTC del servidor a fecha local chilena para mostrar en inputs
 * @param {string} utcDateString - Fecha en formato ISO del servidor
 * @returns {string} Fecha en formato YYYY-MM-DD para inputs
 */
export const utcToChileanInputDate = (utcDateString) => {
  if (!utcDateString) return '';

  // Si viene como YYYY-MM-DD o ISO a medianoche, devolvemos YYYY-MM-DD directamente
  if (isDateOnly(utcDateString)) return utcDateString;
  if (isMidnightISO(utcDateString)) return utcDateString.slice(0, 10);

  const utcDate = new Date(utcDateString);
  // Ajustar a zona horaria chilena
  const chileanDate = new Date(utcDate.toLocaleString('en-US', { timeZone: CHILE_TIMEZONE }));
  const year = chileanDate.getFullYear();
  const month = String(chileanDate.getMonth() + 1).padStart(2, '0');
  const day = String(chileanDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convierte una fecha de input (YYYY-MM-DD) a formato ISO para enviar al servidor
 * @param {string} inputDateString - Fecha del input en formato YYYY-MM-DD
 * @returns {string} Fecha en formato ISO
 */
export const chileanInputDateToISO = (inputDateString) => {
  if (!inputDateString) return '';
  
  const [year, month, day] = inputDateString.split('-').map(Number);

  // Construir ISO consistente sin desfases: usar mediodía UTC directamente
  // De esta manera 2017-03-30 => 2017-03-30T12:00:00.000Z y siempre vuelve a 2017-03-30
  const iso = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0)).toISOString();
  return iso;
};
